import React, { useState, useMemo } from 'react';
import { FilterForm, RecordContextProvider, useListContext, useTranslate } from 'react-admin';
import { Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, IconButton, makeStyles, Paper, Typography } from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import moment from 'moment';

import { SURFACE_EVENT_TYPES } from '@hisports/common/src/constants';

import { InlineDateInput } from '../../../inputs/DateInput';
import { SurfaceField } from '../../../../resources/surfaces/SurfaceField';
import { getEventTypeColor } from '../../../../resources/surfaceEvents/SurfaceEventRow';
import { CategoryInput } from '../../../../resources/categories/CategoryInput';
import { TeamInput } from '../../../../resources/teams/TeamInput';
import { CALENDAR_VIEWS, useCalendarView } from '../../../../resources/events/EventViewSettings';

import { useAssociationSchedulingContext } from './AssociationSchedulingContext';

const useStyles = makeStyles(theme => ({
  filterPaper: {
    width: props => props?.open ? 300 : 45,
    overflow: 'hidden',
    position: 'relative',
    margin: theme.spacing(1),
    padding: theme.spacing(1),
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  filterIcon: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  colorLegend: {
    display: 'inline-block',
    width: 12,
    height: 12,
    borderRadius: 2,
    background: ({ type }) => {
      return type === SURFACE_EVENT_TYPES.AVAILABILITY
        ? `linear-gradient(45deg, ${getEventTypeColor(type, theme, true)} 50%, ${getEventTypeColor(type, theme, false)} 50%)`
        : getEventTypeColor(type, theme)
    },
    border: '1px solid rgba(0,0,0,0.2)',
  },
  formControlLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  filterDiv: {
    paddingTop: theme.spacing(1),
    width: '100%',
  },
  filterField: {
    '& [class*="RaFilterFormInput-body"]': {
      width: '100%',
    },
    '& [class*="RaFilterFormInput-spacer"]': {
      display: 'none',
    },
    '& .MuiAutocomplete-root': {
      width: '100%',
    }
  }
}))

const SurfaceFilter = () => {
  const { surfaces = [] } = useAssociationSchedulingContext();
  const { filterValues, setFilters, loading } = useListContext();
  const translate = useTranslate();

  const selectedArenaIds = useMemo(() => {
    return filterValues?.arenaId?.inq || [];
  }, [filterValues?.arenaId?.inq]);

  const handleToggle = (arenaId) => {
    const updated = selectedArenaIds.includes(arenaId)
      ? selectedArenaIds.filter(id => id !== arenaId)
      : [...selectedArenaIds, arenaId];

    setFilters({ ...filterValues, arenaId: { inq: updated } }, null);
  };

  if (!surfaces?.length) return null
  return (
    <FormControl component="fieldset" style={{ marginTop: 16 }} disabled={loading}>
      <FormLabel component="legend" color="secondary" focused={false}>
        {translate('resources.surfaces.name', { smart_count: 2 })}
      </FormLabel>
      <FormGroup>
        {surfaces.map(surface => (
          <FormControlLabel
            key={surface.id}
            control={
              <Checkbox
                checked={selectedArenaIds.includes(surface.id)}
                onChange={() => handleToggle(surface.id)}
                color="primary"
              />
            }
            label={
              <RecordContextProvider value={surface}>
                <SurfaceField source="id" link={false} />
              </RecordContextProvider>
            }
          />
        ))}
      </FormGroup>
    </FormControl>
  );
};

const ColorLegend = ({ type }) => {
  const classes = useStyles({ type });

  return <span className={classes.colorLegend} />
}

const EventTypeFilter = () => {
  const classes = useStyles();
  const { filterValues, setFilters, loading } = useListContext();
  const translate = useTranslate();

  const selectedTypes = useMemo(() => {
    return filterValues?.type?.inq || [];
  }, [filterValues?.type?.inq]);

  const handleToggle = (type) => {
    const updated = selectedTypes.includes(type)
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];

    setFilters({ ...filterValues, type: { inq: updated } }, null);
  };

  return (
    <FormControl component="fieldset" style={{ marginTop: 16 }} disabled={loading}>
      <FormLabel component="legend" color="secondary" focused={false}>
        {translate('resources.surfaceEvents.fields.type')}
      </FormLabel>
      <FormGroup>
        {Object.values(SURFACE_EVENT_TYPES).map(type => (
          <FormControlLabel
            key={type}
            control={
              <Checkbox
                checked={selectedTypes.includes(type)}
                onChange={() => handleToggle(type)}
                color="primary"
              />
            }
            label={
              <span className={classes.formControlLabel}>
                <Typography variant="body2">
                  {translate(`resources.surfaceEvents.values.type.${type}`)}
                </Typography>
                <ColorLegend type={type} />
              </span>
            }
          />
        ))}
      </FormGroup>
    </FormControl>
  );
};

const inputProps = {
  variant: 'outlined',
  fullWidth: true,
  alwaysOn: true,
}

export const AssociationSchedulerFilters = () => {
  const { filterValues, setFilters } = useListContext();
  const translate = useTranslate();
  const [calendarView] = useCalendarView();
  const [open, setOpen] = useState(true);
  const classes = useStyles({ open })

  const onStartTimeChange = (newStartTime) => {
    if (calendarView !== CALENDAR_VIEWS.ASSOCIATION_CALENDAR) return;

    const startTime = newStartTime ? moment.utc(newStartTime).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    const endTime = moment.utc(startTime).add(5, 'days').format('YYYY-MM-DD');
    setFilters({ ...filterValues, startTime, endTime });
  }

  const timeInputFilters = [
    <InlineDateInput label={translate('resources.surfaceEvents.filters.startTime')} source="startTime" {...inputProps} onChange={onStartTimeChange} />,
    <InlineDateInput label={translate('resources.surfaceEvents.filters.endTime')} source="endTime" {...inputProps} disabled={calendarView === CALENDAR_VIEWS.ASSOCIATION_CALENDAR} />
  ]

  const otherInputFilters = [
    <CategoryInput label={translate('resources.surfaceEvents.filters.categoryId')} source="categoryId" multiple {...inputProps} />,
    <TeamInput label={translate('resources.surfaceEvents.filters.teamId')} source="teamId" multiple {...inputProps} />
  ]

  return (
    <Paper className={classes.filterPaper}>
      <div className={classes.filterIcon}>
        <IconButton
          size="small"
          onClick={() => setOpen(prev => !prev)}
        >
          {open ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </div>

      <div style={{ display: open ? 'block' : 'none' }}>
        <div className={classes.filterDiv}>
          <div className={classes.filterField}>
            <FilterForm filters={timeInputFilters} />
          </div>
        </div>
        <div className={classes.filterDiv}>
          <SurfaceFilter />
        </div>
        <div className={classes.filterDiv}>
          <EventTypeFilter />
        </div>
        <div className={classes.filterDiv}>
          <div className={classes.filterField}>
            <FilterForm filters={otherInputFilters} />
          </div>
        </div>
      </div>
    </Paper>
  );
};
